import toNumber from 'lodash/toNumber'
import { roundNumber } from '@ztechapps/common.utils'

/** Remark stored when available stock cannot cover the ordered quantity. */
export const OUT_OF_STOCK_REMARK = 'Out of Stock'
/** Remark stored when available stock exceeds the itemized threshold. */
export const IN_STOCK_REMARK = 'In Stock'
/** Highest count shown as an exact number; above this the remark is the generic `In Stock`. */
export const STOCK_REMARK_MAX_ITEMIZED = 4

const buildItemizedStockRemark = (count: number): string => `${count} In Stock`

/**
 * Every value `computeStockRemark` can emit — the single source of truth the
 * template-seeding migration imports, so the admin `** ` prefix match (stored value ⇔
 * `order-item-remarks_template` row) can never silently drift from the generator.
 */
export const STOCK_REMARK_VALUES: string[] = [
  OUT_OF_STOCK_REMARK,
  ...Array.from({ length: STOCK_REMARK_MAX_ITEMIZED }, (_, index) => buildItemizedStockRemark(index + 1)),
  IN_STOCK_REMARK,
]

/**
 * `available` = on-hand stock + BOM-buildable stock; `ordered` = the order quantity
 * converted to base units. "Out of Stock" means available stock cannot cover the
 * ordered quantity (the existing insufficiency rule, generalized here with count tiers
 * on top — not replaced). The displayed count is rounded to a whole number. Returns
 * plain text — the admin Remarks cell prefixes "** " at display; storefront and PDF
 * render the stored value verbatim.
 */
export const computeStockRemark = (available: number, ordered: number): string => {
  const availableNumber = toNumber(available)
  const orderedNumber = toNumber(ordered)

  if (!Number.isFinite(availableNumber) || availableNumber <= 0 || availableNumber < orderedNumber) {
    return OUT_OF_STOCK_REMARK
  }
  if (availableNumber <= STOCK_REMARK_MAX_ITEMIZED) return buildItemizedStockRemark(roundNumber(availableNumber))
  return IN_STOCK_REMARK
}
